import React, { useCallback, useState, useEffect } from "react";
import { apiFetch } from "../lib/api.js";
import { Users, DollarSign, Target, Activity, Sparkles, RefreshCw, UserPlus, BarChart3, Landmark, FileText, Clock, AlertTriangle, CheckCircle2, TrendingUp, ChevronDown, LayoutDashboard } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import {
  KartaPrehledu, HlavickaDlazdice, SkupinaPrehledu, RadkyPrehledu,
  RadekPrehledu, MrizkaDlazdic, MiniDlazdice, SouhrnnyRadek, VelkeCislo, Pilulka,
  HlaskaDlazdice,
} from "../components/PrehledDlazdice.jsx";
import GoalsCard from "../components/GoalsCard.jsx";
import { getCurrentUser } from "../lib/auth.js";
import Card from "../components/Card.jsx";
import InfoTip from "../components/InfoTip.jsx";
import NotificationsPanel from "../components/NotificationsPanel.jsx";
import MesicniPriority from "../components/MesicniPriority.jsx";
import DlazdiceSales from "../components/sales/DlazdiceSales.jsx";
import { useApiData } from "../lib/api.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { mockOverview } from "../data/mockData.js";
import { fmt } from "../lib/format.js";
import { useNotifications } from "../lib/AlertContext.jsx";
import ClientLink from "../components/ClientLink.jsx";
import { toast } from "../components/Toast.jsx";



// InfoTip je sdílená komponenta — importujeme z /components/InfoTip.jsx místo lokální duplikáty.
// Duplicitní lokální verze zde odstraněna (měla starý bug s uppercase dědicím z parent labelu).
// NEPŘEPSÁVAT zpět lokálně — všechny opravy se dělají v /components/InfoTip.jsx jedenkrát.

// Rozbalovací řádek s jmény
// Blok varování o nepárovaných klientech, kliknutím se rozbalí seznam jmen
function UnmatchedNotice({ total, matched, names }) {
  const [open, setOpen] = React.useState(false);
  const unmatchedCount = (names || []).length;
  if (unmatchedCount === 0) return null;
  return (
    <div className="mt-2 bg-amber-50 border border-amber-300 rounded-xl overflow-hidden">
      <div
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer select-none hover:bg-amber-100/60 transition-colors"
      >
        <div className="flex items-center gap-2 text-amber-800 text-sm">
          <ChevronDown className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${open ? "" : "-rotate-90"}`} />
          <span className="font-semibold">{unmatchedCount} z {total} dealů</span>
          <span className="text-amber-700 font-normal">nemá v tomto měsíci vystavenou fakturu</span>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-2 pt-1 border-t border-amber-200 bg-amber-50/50 space-y-1">
          {names.map((n, i) => (
            <div key={i} className="text-sm text-amber-900 flex items-center gap-1.5">
              <span className="text-amber-500">•</span>
              <ClientLink name={n} className="text-amber-900 hover:text-amber-700 font-medium" />
            </div>
          ))}
          <div className="text-sm text-amber-700/80 italic mt-2 pt-1.5 border-t border-amber-200/60">
            Ověř v kartě klienta a ve FAPI. Klient buď ještě nemá vystavenou fakturu za balíček, nebo má na faktuře odlišný zápis jména.
          </div>
        </div>
      )}
    </div>
  );
}

// Skupina Výsledek v dlaždici Aktivní klienti: dlaždičky s čísly, kliknutím
// se pod mřížkou rozbalí jména. Obsah i nápovědy zůstávají beze změny.
function DlazdiceVysledku({ polozky, rate }) {
  const [otevrene, setOtevrene] = React.useState(null);
  const vybrana = polozky.find((x) => x.klic === otevrene);
  // Dlaždice Přehledu jsou v mřížce po třetinách, na čtyři sloupce tady není
  // místo ani na velkém monitoru. Dvě řady po dvou drží popisky celé.
  const sloupce = "grid-cols-2";
  return (
    <div className="px-3 py-3 sm:px-4">
      <MrizkaDlazdic sloupce={sloupce}>
        {polozky.map((x) => {
          const maJmena = x.names && x.names.length > 0;
          const obsah = (
            <MiniDlazdice
              hodnota={x.count}
              popisek={x.label}
              napoveda={x.tip}
              ton={x.ton}
            />
          );
          if (!maJmena) return <div key={x.klic}>{obsah}</div>;
          return (
            /* 14.9.2026 (fáze 6a): div s onClick místo tlačítka. Uvnitř je
               nápověda, která je sama tlačítko, a tlačítko v tlačítku není
               platné HTML. Klepnutí na nápovědu si řeší nápověda sama. */
            <div
              key={x.klic}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                if (e.target.closest && e.target.closest("button, a")) return;
                setOtevrene((o) => (o === x.klic ? null : x.klic));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOtevrene((o) => (o === x.klic ? null : x.klic)); }
              }}
              aria-expanded={otevrene === x.klic}
              className={`block h-full w-full cursor-pointer rounded-xl text-left transition focus:outline-none ${
                otevrene === x.klic ? "ring-2 ring-brand/40" : "sm:hover:brightness-95"
              }`}
            >
              {obsah}
            </div>
          );
        })}
      </MrizkaDlazdic>
      {vybrana && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
            {vybrana.label}
          </div>
          <div className="space-y-0.5">
            {vybrana.names.map((n, i) => (
              <div key={i} className="text-sm text-slate-700">
                <ClientLink name={n} className="text-slate-700 sm:hover:text-brand-deep" />
              </div>
            ))}
          </div>
        </div>
      )}
      {rate !== null && (
        <SouhrnnyRadek
          className="mt-2"
          popisek="Úspěšnost uzavírání"
          napoveda="Poměr nových klientů ku všem realizovaným trialům (dealy + prohra + nerozhodnutí)."
          hodnota={`${Math.round(rate * 100)} %`}
          ton={rate >= 0.7 ? "zelena" : rate >= 0.5 ? "zluta" : "cervena"}
        />
      )}
    </div>
  );
}

// Zelená sekce: deal / prohra / nerozhodnuté + úspěšnost
function FunnelBreakdown({ d }) {
  const total = d.trialDealCount + d.trialLossCount + (d.trialUndecidedCount || 0);
  const rate = total > 0 ? d.trialDealCount / total : 0;
  const polozky = [];
  if (d.trialDealCount > 0) polozky.push({
    klic: "deal", label: "Deal z trialu", count: d.trialDealCount, ton: "zelena",
    names: d.trialDealNames,
    tip: "Klienti, kteří měli realizovaný trial v tomto měsíci a v Leady mají stav Deal nebo Uzavřeno. Trial-atribuce, klient se mohl dostat do firmy jako lead v minulém měsíci, důležité je že trial proběhl TENTO měsíc. Pozn.: Funnel vedle používá lead-atribuci (lead vytvořen tento měsíc) a typicky ukazuje méně dealů, protože někteří leadové z minulých měsíců měli trial teď a propadnou sem, ale ne do Funnelu.",
  });
  if (d.trialLossCount > 0) polozky.push({
    klic: "prohra", label: "Prohra", count: d.trialLossCount, ton: "cervena",
    names: d.trialLossNames,
    tip: "Realizované trialy tento měsíc, které mají v Leady stav Prohra. Trial-atribuce (rozhodnutí padlo teď, lead mohl být i starší). Funnel používá lead-atribuci a může ukázat míň proher ze stejného důvodu jako u Dealů.",
  });
  if ((d.trialUndecidedCount || 0) > 0) polozky.push({
    klic: "nerozhodnuti", label: "Nerozhodnutých", count: d.trialUndecidedCount, ton: "slate",
    names: d.trialUndecidedNames,
    tip: "Realizované trialy, které v Leady nemají ani stav Deal/Uzavřeno, ani Prohra, stále se rozhodují.",
  });
  if ((d.trialPendingNextCount || 0) > 0) polozky.push({
    klic: "ceka", label: "Čeká na další trial", count: d.trialPendingNextCount, ton: "slate",
    names: d.trialPendingNextNames,
    tip: "Klienti, kteří už absolvovali zkušební trénink a mají naplánovaný další (do 30 dní). Ještě se nerozhodli, pokračujeme v procesu.",
  });
  const ukazatRate = d.trialDealCount > 0 && (d.trialLossCount > 0 || (d.trialUndecidedCount || 0) > 0);
  return <DlazdiceVysledku polozky={polozky} rate={ukazatRate ? rate : null} />;
}

// Dlaždice Aktivní klienti. Obsah, čísla i nápovědy jsou stejné jako dřív,
// mění se jen tvar podle odsouhlaseného návrhu (hlavička, skupiny, řádky).
export function DlazdiceAktivniKlienti({ d, sheetLive, notionLive }) {
  return (
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Users}
          nazev="Aktivní klienti"
          napoveda={'Klienti se stavem "Aktivní" v sekci Klienti.'}
          odznak={sheetLive ? "Live z databáze" : (notionLive ? "Live z Notion" : "Mock")}
          odznakZivy={sheetLive || notionLive}
        />
        <div className="p-3 sm:p-4">
          <MrizkaDlazdic
            sloupce={
              (d.newThisMonth > 0 ? 1 : 0) + (d.endingThisMonth > 0 ? 1 : 0) === 2
                ? "grid-cols-3"
                : (d.newThisMonth > 0 || d.endingThisMonth > 0 ? "grid-cols-2" : "grid-cols-1")
            }
          >
            <MiniDlazdice
              velka
              hodnota={fmt(d.activeClients)}
              popisek="aktivních klientů"
              ton="neutral"
            />
            {d.newThisMonth > 0 && (
              <MiniDlazdice
                velka
                hodnota={d.newThisMonth}
                popisek="nových tento měsíc"
                napoveda={'Aktivní klienti, jejichž "Začátek spolupráce" spadá do aktuálního měsíce. Zdroj: karty klientů.'}
                ton="zelena"
              />
            )}
            {d.endingThisMonth > 0 && (
              <MiniDlazdice
                velka
                hodnota={d.endingThisMonth}
                popisek="potenciálně končících"
                napoveda={'Aktivní klienti, kterým datum "Konec spolupráce" spadá do aktuálního měsíce, v tomto měsíci se rozhoduje, jestli budou pokračovat. Zdroj: karty klientů.'}
                ton="zluta"
              />
            )}
          </MrizkaDlazdic>
        </div>

        {d.leadsThisMonth > 0 && (
          <>
            <SkupinaPrehledu ikona={UserPlus} nazev="Leady" />
            <RadkyPrehledu>
              <RadekPrehledu
                popisek="Nových leadů"
                napoveda="Počet nových leadů vytvořených tento měsíc."
                hodnota={fmt(d.leadsThisMonth)}
              />
              {d.fapiTrialCount > 0 && (
                <RadekPrehledu
                  popisek="Konverze na trial"
                  napoveda="Kolik % leadů si objednalo zkušební trénink. Počítáno: FAPI faktury za zkušební / počet leadů."
                  hodnota={`${Math.round(d.fapiTrialCount / d.leadsThisMonth * 100)} %`}
                  procenta={Math.round(d.fapiTrialCount / d.leadsThisMonth * 100)}
                  tonPruhu="bg-brand-400"
                />
              )}
            </RadkyPrehledu>
          </>
        )}

        {(d.fapiTrialCount > 0 || d.realizedTrialCount > 0) && (
          <>
            <SkupinaPrehledu ikona={Sparkles} nazev="Trialy" />
            <RadkyPrehledu>
              {d.fapiTrialCount > 0 && (
                <RadekPrehledu
                  popisek="Zaplaceno tento měsíc"
                  napoveda="Počet zaplacených faktur za zkušební trénink, které byly vystavené v tomto měsíci (sledujeme datum vystavení, ne datum zaplacení). U vás se trialy platí instantně kartou, takže vystavení = zaplacení ve stejný den. Klient může mít termín trialu v jiném měsíci, proto se nemusí rovnat Realizovaným trialům."
                  hodnota={fmt(d.fapiTrialCount)}
                />
              )}
              {(d.realizedUniqueCount || d.realizedTrialCount) > 0 && (
                <RadekPrehledu
                  popisek="Realizovaných"
                  napoveda="Unikátní klienti s potvrzeným zkušebním tréninkem v Reservio, který už proběhl (datum < dnes). Součet = Nových klientů + Prohra + Nerozhodnutí + Čeká na další trial."
                  doplnek={
                    d.fapiTrialCount > 0 && d.fapiTrialCount !== (d.realizedUniqueCount || d.realizedTrialCount)
                      ? `rozdíl ${d.fapiTrialCount > (d.realizedUniqueCount || d.realizedTrialCount) ? "+" : "-"}${Math.abs(d.fapiTrialCount - (d.realizedUniqueCount || d.realizedTrialCount))}`
                      : (d.realizedDuplicates > 0 ? `${d.realizedDuplicates}× dupl.` : undefined)
                  }
                  doplnekNapoveda={
                    d.fapiTrialCount > 0 && d.fapiTrialCount !== (d.realizedUniqueCount || d.realizedTrialCount)
                      ? "Někteří klienti zaplatili v jiném měsíci, než ve kterém trial proběhl (platba koncem předchozího měsíce / termín v budoucnu), nebo po zaplacení zrušili termín. Případné nesrovnalosti uvidíš jako notifikace ve zvonečku nahoře."
                      : undefined
                  }
                  hodnota={fmt(d.realizedUniqueCount || d.realizedTrialCount)}
                />
              )}
              {d.trialDealCount > 0 && (d.realizedUniqueCount || d.realizedTrialCount) > 0 && (
                <RadekPrehledu
                  popisek="Konverze na deal"
                  napoveda="Kolik % realizovaných trialů začalo spolupráci. Počítáno: dealy / realizované trialy."
                  hodnota={`${Math.round(d.trialDealCount / (d.realizedUniqueCount || d.realizedTrialCount) * 100)} %`}
                  procenta={Math.round(d.trialDealCount / (d.realizedUniqueCount || d.realizedTrialCount) * 100)}
                />
              )}
            </RadkyPrehledu>
          </>
        )}

        {(d.trialDealCount > 0 || d.trialLossCount > 0 || d.trialUndecidedCount > 0) && (
          <>
            <SkupinaPrehledu ikona={CheckCircle2} nazev="Výsledek" />
            <FunnelBreakdown d={d} />
          </>
        )}
      </KartaPrehledu>
  );
}

// Dlaždice Měsíční revenue. Stejné řádky i nápovědy, jen částka je na jednom
// řádku a počty faktur a DPH jsou pilulky místo textu za částkou.
export function DlazdiceRevenue({ d, fapiLive }) {
  const dph = (d.monthlyRevenueLive || 0) - (d.monthlyRevenueNoVat || 0);
  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={DollarSign}
        nazev="Měsíční revenue"
        napoveda="Celková fakturovaná částka s DPH z FAPI za aktuální měsíc. Zahrnuje všechny vystavené faktury, zaplacené i nezaplacené. Zdroj: FAPI API, filtr datum vystavení."
        odznak={fapiLive ? "Live z FAPI" : "Mock"}
        odznakZivy={fapiLive}
      />
      <VelkeCislo
        hodnota={fmt(d.monthlyRevenueLive)}
        jednotka="Kč"
        pilulky={fapiLive ? [
          { text: `${d.totalInvoices || 0} faktur` },
          { text: `z toho DPH ${fmt(dph)} Kč` },
        ] : []}
      />

      {fapiLive && (
        <>
          <SkupinaPrehledu ikona={FileText} nazev="Stav plateb" />
          <RadkyPrehledu>
            <RadekPrehledu
              popisek="Zaplaceno"
              napoveda="Částka, kterou už klienti skutečně uhradili na účet. Z vystavených faktur tohoto měsíce to jsou ty se statusem zaplaceno v FAPI. Zdroj: FAPI API."
              doplnek={`${d.fapiPaidCount} faktur`}
              hodnota={fmt(d.fapiPaidRevenue)}
              tonHodnoty="text-emerald-700"
            />
            <RadekPrehledu
              popisek="Na cestě"
              napoveda="Vystavené faktury, které ještě nebyly zaplacené, ale nejsou po splatnosti. Peníze se očekávají. Zdroj: FAPI, status není zaplaceno a datum splatnosti je v budoucnu."
              doplnek={`${d.fapiOnTheWayCount} faktur`}
              hodnota={fmt(d.fapiOnTheWayRevenue)}
              tonHodnoty="text-amber-700"
            />
            {d.fapiPastDueCount > 0 && (
              <RadekPrehledu
                popisek="Po splatnosti"
                napoveda="Vystavené faktury, kterým už uběhlo datum splatnosti a pořád nejsou zaplacené. Nutno klientům připomenout. Zdroj: FAPI."
                doplnek={`${d.fapiPastDueCount} faktur`}
                hodnota={fmt(d.fapiPastDueRevenue)}
                tonHodnoty="text-rose-700"
              />
            )}
          </RadkyPrehledu>

          {(d.newClientRevenue > 0 || d.fapiTrialCount > 0 || d.fapiOnlineCount > 0) && (
            <>
              <SkupinaPrehledu ikona={BarChart3} nazev="Odkud peníze jsou" />
              <RadkyPrehledu>
                {d.newClientRevenue > 0 && (
                  <RadekPrehledu
                    popisek="Noví klienti"
                    napoveda="Revenue z faktur od klientů, kteří začali spolupráci v tomto měsíci (podle začátku spolupráce v kartě klienta). Nezahrnuje zkušební tréninky. Zdroj: FAPI a karty klientů."
                    hodnota={fmt(d.newClientRevenue)}
                    pilulky={[
                      { text: `${d.newClientCount} klientů` },
                      { text: `DPH ${fmt(d.newClientRevenueVat)}` },
                    ]}
                  />
                )}
                {d.fapiTrialCount > 0 && (
                  <RadekPrehledu
                    popisek="Zkušební tréninky"
                    napoveda="Revenue ze zaplacených faktur za zkušební trénink vystavených v tomto měsíci. DPH vyčísleno zvlášť. Zdroj: FAPI, položka obsahující slovo zkušebn, kromě Klubu FitWay40."
                    hodnota={fmt(d.fapiTrialRevenue)}
                    pilulky={[
                      { text: `${d.fapiTrialCount} trialů` },
                      { text: `DPH ${fmt(d.fapiTrialRevenueVat)}` },
                    ]}
                  />
                )}
                {/* 30. 10. 2026: Online produkty, Klub FitWay40 */}
                {d.fapiOnlineCount > 0 && (
                  <RadekPrehledu
                    popisek="Online produkty"
                    napoveda="Revenue ze zaplacených faktur za Klub FitWay40 vystavených v tomto měsíci. Nepočítá se do osobních tréninků, zkušebních tréninků ani nových klientů. Zdroj: FAPI, položka Klub FitWay40."
                    hodnota={fmt(d.fapiOnlineRevenue)}
                    pilulky={[
                      { text: "Klub FitWay40" },
                      { text: d.fapiOnlineCount === 1 ? "1 faktura" : (d.fapiOnlineCount >= 2 && d.fapiOnlineCount <= 4 ? `${d.fapiOnlineCount} faktury` : `${d.fapiOnlineCount} faktur`) },
                      { text: `DPH ${fmt(d.fapiOnlineRevenueVat)}` },
                    ]}
                  />
                )}
              </RadkyPrehledu>
            </>
          )}

          {(d.trialLossCount || 0) > 0 && (
            <div className="px-3 py-3 sm:px-4">
              <SouhrnnyRadek
                ton="cervena"
                popisek="Ztracený obrat"
                napoveda="Odhad obratu, který nám unikl kvůli prohrám. Pro každou prohru tohoto měsíce počítáme, že by klient přinesl 6 700 Kč (odhad průměrného měsíčního výdělku). Slouží jako orientační ukazatel, ne účetní údaj."
                doplnek={`${d.trialLossCount} × 6 700`}
                hodnota={`−${fmt(d.trialLossCount * 6700)}`}
              />
            </div>
          )}
        </>
      )}
    </KartaPrehledu>
  );
}

// Dlaždice MKT spent. Devět čísel rozdělených na dvě otázky: co stojí jeden
// lead, trial a klient, a co se z toho vrací. ROI je jednou, dole.
export function DlazdiceMkt({ d, metaLive }) {
  const roiRevenue = d.trialDealRevenueNoVat || 0;
  const roiPct = (d.mktSpendLive > 0 && roiRevenue > 0)
    ? Math.round((roiRevenue / d.mktSpendLive) * 100)
    : 0;
  const cpl14d = (d.mktSpendLast14d != null && d.leadsLast14d > 0)
    ? Math.round(d.mktSpendLast14d / d.leadsLast14d)
    : null;
  // Hranice 900 Kč: nad ní je akvizice přes Meta neekonomická a je potřeba to řešit.
  const cplVysoky = cpl14d != null && cpl14d >= 900;
  const jeCoStoji = metaLive && d.mktSpendLive > 0
    && (cpl14d != null || d.leadsThisMonth > 0 || d.fapiTrialCount > 0 || d.trialDealCount > 0);
  const jeCoSeVraci = metaLive && d.mktSpendLive > 0 && d.trialDealCount > 0 && d.trialDealRevenue > 0;

  const pilulkyZahlavi = [{ text: "tento měsíc" }];
  if (d.sources?.meta?.period) pilulkyZahlavi.push({ text: `od ${formatDatumCesky(d.sources.meta.period.since)}` });

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Target}
        nazev="MKT spent"
        napoveda="Výdaje na marketing za aktuální měsíc. Zdroj: Meta Ads API. OD 1.1.2026: počítáme JEN whitelistované kampaně přes env var META_AD_CAMPAIGN_NAME_INCLUDE v Netlify (substring match, case insensitive, ignoruje diakritiku). Aktuální nastavení = [LEAD] matchne všechny kampaně co mají v názvu [LEAD], [Lead] nebo [lead]. Vyřadí kampaně bez tohoto prefixu (např. Jídelníček, HR nábor). Když přidáš novou tréninkovou kampaň, ujisti se že má v názvu [LEAD]. Pro víc nezávislých patternů použij comma-separated v env var. Před 1.1.2026: account-level sum všech kampaní (legacy). Nezahrnuje výdaje za správu reklam."
        odznak={metaLive ? "Live z Meta" : "Mock"}
        odznakZivy={metaLive}
      />
      <VelkeCislo hodnota={fmt(d.mktSpendLive)} jednotka="Kč" pilulky={pilulkyZahlavi} />

      {metaLive && cplVysoky && (
        <HlaskaDlazdice
          ikona={AlertTriangle}
          nadpis="CPL kriticky vysoký"
          hodnota={`${fmt(cpl14d)} Kč`}
          pilulky={[`${d.leadsLast14d} leadů za 14 dní`, "hranice 900 Kč"]}
          text="Reklamu je potřeba řešit"
          napoveda="CPL (Cost per Lead) za posledních 14 dní překročil 900 Kč, hranici, kdy akvizice přes Meta začíná být neekonomická. Zkontroluj kampaně: nefunkční kreativy, špatné cílení, příliš úzké publikum. Počítáno: Meta spend za 14 dní / počet leadů za 14 dní."
        />
      )}

      {jeCoStoji && (
        <>
          <SkupinaPrehledu ikona={DollarSign} nazev="Co stojí jeden" />
          <RadkyPrehledu>
            {cpl14d != null && !cplVysoky && (
              <RadekPrehledu
                popisek="Lead za 14 dní"
                napoveda="Cost per Lead za posledních 14 dní, kolik průměrně stojí získání jednoho leadu. Počítáno: Meta spend za 14 dní / počet nových leadů za 14 dní. Reaguje rychleji než měsíční číslo, vidíš aktuální trend. Při překročení 900 Kč se objeví upozornění s výzvou řešit reklamu."
                doplnek={`${d.leadsLast14d} leadů`}
                hodnota={`${fmt(cpl14d)} Kč`}
              />
            )}
            {d.leadsThisMonth > 0 && (
              <RadekPrehledu
                popisek="Lead za měsíc"
                napoveda="Cost per Lead za celý aktuální měsíc, kolik průměrně stojí získání jednoho leadu. Počítáno: MKT spend tento měsíc / počet nových leadů tento měsíc. Srovnej s Lead za 14 dní: pokud je měsíční vyšší, nedávné kampaně šly lépe než starší; pokud nižší, naopak."
                doplnek={`${d.leadsThisMonth} leadů`}
                hodnota={`${fmt(Math.round(d.mktSpendLive / d.leadsThisMonth))} Kč`}
              />
            )}
            {d.fapiTrialCount > 0 && (
              <RadekPrehledu
                popisek="Zkušební trénink"
                napoveda="Kolik stojí získání jednoho klienta, který zaplatil zkušební trénink. Počítáno: MKT spend tento měsíc / počet zaplacených trialů tento měsíc. Ukazuje efektivitu cesty z reklamy až po první platbu."
                doplnek={`${d.fapiTrialCount} trialů`}
                hodnota={`${fmt(Math.round(d.mktSpendLive / d.fapiTrialCount))} Kč`}
              />
            )}
            {d.trialDealCount > 0 && (
              <RadekPrehledu
                popisek="Nový klient"
                napoveda="Kolik stojí získání jednoho nového klienta z dealů tohoto měsíce. Počítáno: MKT spend tohoto měsíce / počet dealů z trialů (realizovaný trial v tomto měsíci → Deal/Uzavřeno v Leady). Nejdůležitější metrika efektivity marketingu, atribuce přesně odpovídá MKT spendu tohoto měsíce."
                doplnek={`${d.trialDealCount} dealů`}
                hodnota={`${fmt(Math.round(d.mktSpendLive / d.trialDealCount))} Kč`}
              />
            )}
          </RadkyPrehledu>
        </>
      )}

      {jeCoSeVraci && (
        <>
          <SkupinaPrehledu ikona={TrendingUp} nazev="Co se vrací" />
          <RadkyPrehledu>
            <RadekPrehledu
              popisek="Ø deal s DPH"
              napoveda="Průměrná hodnota jednoho dealu z trialů tohoto měsíce včetně DPH. Počítáno: obrat z FAPI faktur klientů, jejichž realizovaný trial proběhl v tomto měsíci a v Leady jsou označeni Deal/Uzavřeno / počet těchto dealů. Atribuce odpovídá MKT spendu tohoto měsíce."
              doplnek={`${d.trialDealCount} dealů`}
              hodnota={`${fmt(Math.round(d.trialDealRevenue / d.trialDealCount))} Kč`}
            />
          </RadkyPrehledu>
        </>
      )}

      {metaLive && d.mktSpendLive > 0 && roiRevenue > 0 && (
        <div className="px-3 py-3 sm:px-4">
          <SouhrnnyRadek
            popisek="ROI"
            napoveda="Poměr Ø dealu bez DPH a Ø ceny za nového klienta. Vzorec: (Ø deal s DPH bez DPH / Cost na nového klienta) × 100. Pro 197 % platí: 1 Kč investovaná do MKT vrátí 1,97 Kč (bez DPH). Zelená 300 % a víc, žlutá 150 až 300 %, rudá pod 150 %."
            doplnek={roiPct >= 300 ? "nad 300 je výborné" : roiPct >= 150 ? "150 až 300 je slušné" : "pod 150 je málo"}
            hodnota={`${roiPct} %`}
            ton={roiPct >= 300 ? "zelena" : roiPct >= 150 ? "zluta" : "cervena"}
          />
        </div>
      )}

      {metaLive && d.trialDealCount > 0 && (d.trialDealUnmatchedNames || []).length > 0 && (
        <div className="px-3 pb-3 sm:px-4">
          <UnmatchedNotice
            total={d.trialDealCount}
            matched={d.trialDealMatchedCount}
            names={d.trialDealUnmatchedNames}
          />
        </div>
      )}
    </KartaPrehledu>
  );
}

// Dlaždice Přehled trialů. Termíny a peníze jsou dvě skupiny, seznam termínů
// se rozbaluje pod řádkem, kterého se týká.
export function DlazdiceTrialy({ d, zivo }) {
  const [otevreno, setOtevreno] = React.useState(false);
  const razene = (d.pendingTrialEntries30d || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const prvni = razene[0];
  const prvniPopis = prvni ? `první ${formatTrialDate(prvni.date)}` : "";
  const jsouTerminy = (d.pendingTrialCount14d > 0 || d.pendingTrialCount30d > 0);
  const jsouPenize = (d.newClientRevenue > 0 || d.fapiTrialRevenue > 0 || jsouTerminy);

  const pilulky = [];
  if (d.realizedTrialCount > 0) pilulky.push({ text: `${d.realizedTrialCount} realizovaných` });
  if (d.realizedDuplicates > 0) pilulky.push({ text: `${d.realizedDuplicates} duplicit` });

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Sparkles}
        nazev="Přehled trialů"
        napoveda="Zaplacené faktury za zkušební trénink z FAPI tento měsíc. Realizované = potvrzené v Reservio s datem ≤ dnes."
        odznak={zivo ? "Live" : "Mock"}
        odznakZivy={zivo}
      />
      <VelkeCislo hodnota={fmt(d.fapiTrialCount || 0)} jednotka="zaplacených trialů" pilulky={pilulky} />

      {jsouTerminy && (
        <>
          <SkupinaPrehledu ikona={Clock} nazev="Naplánované termíny" />
          <div className="px-4 sm:px-5">
            <div
              role="button"
              tabIndex={0}
              onClick={(e) => { if (e.target.closest && e.target.closest("button, a")) return; setOtevreno((o) => !o); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOtevreno((o) => !o); } }}
              aria-expanded={otevreno}
              className="flex w-full cursor-pointer items-center justify-between gap-2 py-2.5 text-left text-sm"
            >
              <span className="min-w-0 text-slate-600">
                Do 14 dní
                <InfoTip text="Naplánované zkušební tréninky v Reservio v příštích 14 dnech, ale POUZE klienti kteří už mají uhrazenou trial fakturu ve FAPI. Klienti bez zaplaceného trialu se zde neukazují (lead je v procesu, neumíme z něj zatím počítat potenciál). Match podle jména klienta (sorted tokens). Pokud někoho nevidíš, zkontroluj zda se shoduje jeho jméno v Reservio s FAPI fakturou nebo doplň alias v UI Pravidla." />
                {prvniPopis && <span className="ml-2 inline-flex rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{prvniPopis}</span>}
                {d.pendingDuplicates14d > 0 && <span className="ml-2 inline-flex rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{d.pendingDuplicates14d} duplicit</span>}
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                <span className="text-base font-bold tabular-nums text-slate-900">{d.pendingTrialCount14d}</span>
                <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${otevreno ? "rotate-180" : ""}`} />
              </span>
            </div>
          </div>
          {otevreno && razene.length > 0 && (
            <div className="max-h-64 overflow-y-auto border-y border-slate-200 bg-slate-50">
              {razene.map((e, idx) => (
                <div
                  key={`${e.norm}-${e.date}-${idx}`}
                  className={`flex items-center justify-between gap-2 px-4 py-2 text-sm sm:px-5 ${idx > 0 ? "border-t border-slate-200" : ""}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-slate-700 demo-sensitive">{e.name}</span>
                    {e.trener && <span className="block truncate text-xs text-slate-500">{e.trener}{e.pobocka ? `, ${e.pobocka}` : ""}</span>}
                  </span>
                  <span className="shrink-0 text-right tabular-nums text-slate-500">
                    {formatTrialDate(e.date)}
                    {relativeDayLabel(e.date) && <span className="ml-1 text-slate-400">{relativeDayLabel(e.date)}</span>}
                    {e.cas && <span className="block text-xs text-slate-400">{e.cas}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
          {d.pendingTrialCount30d > d.pendingTrialCount14d && (
            <div className="px-4 sm:px-5">
              <div className="flex items-center justify-between gap-2 border-t border-slate-200 py-2.5 text-sm">
                <span className="text-slate-600">Do 30 dní</span>
                <span className="text-base font-bold tabular-nums text-slate-900">{d.pendingTrialCount30d}</span>
              </div>
            </div>
          )}
        </>
      )}

      {jsouPenize && (
        <>
          <SkupinaPrehledu ikona={DollarSign} nazev="Peníze z trialů" />
          <RadkyPrehledu>
            {(d.newClientRevenue > 0 || d.fapiTrialRevenue > 0) && (
              <RadekPrehledu
                popisek="Zisk z dealů"
                napoveda="Revenue od klientů, kteří v tomto měsíci začali spolupráci, plus zaplacené zkušební tréninky. Zdroj: FAPI."
                doplnek={`DPH ${fmt(d.newClientRevenueVat + d.fapiTrialRevenueVat)}`}
                hodnota={`${fmt(d.newClientRevenue + d.fapiTrialRevenue)} Kč`}
              />
            )}
            {d.pendingTrialCount14d > 0 && (
              <RadekPrehledu
                popisek="Potenciál do 14 dní"
                napoveda="Potenciální výnos z naplánovaných trialů na příštích 14 dní × průměrná hodnota klienta (6 700 Kč vč. DPH). Počítá se POUZE z klientů, kteří už mají uhrazenou trial fakturu ve FAPI. Klienty bez zaplaceného trialu sem nezahrnujeme, protože z nezaplaceného trialu se těžko stane deal."
                hodnota={`${fmt(d.pendingTrialCount14d * 6700)} Kč`}
              />
            )}
            {d.pendingTrialCount30d > d.pendingTrialCount14d && (
              <RadekPrehledu
                popisek="Potenciál do 30 dní"
                napoveda="Totéž jako potenciál do 14 dní, jen počítané z termínů na příštích 30 dní. Ø 6 700 Kč na trial vč. DPH."
                hodnota={`${fmt(d.pendingTrialCount30d * 6700)} Kč`}
              />
            )}
          </RadkyPrehledu>
        </>
      )}
    </KartaPrehledu>
  );
}

// Odstíny značky seřazené od nejtmavšího, kategorie grafu se barví podle
// velikosti, ne náhodně. Nahrazuje starou duhovou paletu.
const ODSTINY_ZNACKY = ["#5F4690", "#7C5DB0", "#9B7FC7", "#C5B3E3", "#DFD4F2"];

// Dlaždice Zdroje kontaktu. Leady vytvořené tento měsíc, řádky s proužkem.
export function DlazdiceZdroje({ d, sheetLive }) {
  const zdroje = d.contactSources || [];
  const celkem = zdroje.reduce((s, e) => s + e.count, 0);
  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={BarChart3}
        nazev="Zdroje kontaktu"
        odznak={sheetLive ? "Live z databáze" : "Mock"}
        odznakZivy={sheetLive}
      />
      {zdroje.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm italic text-slate-400 sm:px-5">
          Čeká na data, tento měsíc zatím nepřišel žádný nový lead.
        </div>
      ) : (
        <>
          <VelkeCislo
            hodnota={fmt(celkem)}
            jednotka="nových leadů"
            pilulky={[
              { text: "tento měsíc" },
              { text: `${zdroje.length} zdrojů` },
            ]}
          />
          <SkupinaPrehledu ikona={Landmark} nazev="Odkud přišli" />
          <RadkyPrehledu>
            {zdroje.map((src, i) => {
              const pct = celkem > 0 ? Math.round((src.count / celkem) * 100) : 0;
              return (
                <RadekPrehledu
                  key={i}
                  popisek={src.name}
                  doplnek={`${pct} %`}
                  hodnota={fmt(src.count)}
                  pruh={pct}
                  barvaPruhu={ODSTINY_ZNACKY[Math.min(i, ODSTINY_ZNACKY.length - 1)]}
                />
              );
            })}
          </RadkyPrehledu>
        </>
      )}
    </KartaPrehledu>
  );
}

// Krok trychtýře: název, proužek podílu, číslo. Na telefonu pod sebou,
// na počítači v jednom řádku.
function KrokFunnelu({ nazev, napoveda, hodnota, podil, barva, tonCisla = "text-slate-900" }) {
  return (
    <div className="space-y-1 sm:flex sm:items-center sm:gap-3 sm:space-y-0">
      <span className="flex items-baseline justify-between text-sm font-semibold text-slate-700 sm:w-24 sm:shrink-0 sm:justify-start">
        <span>
          {nazev}
          {napoveda && <InfoTip text={napoveda} />}
        </span>
        <span className={`text-xl font-bold leading-none tabular-nums sm:hidden ${tonCisla}`}>{hodnota}</span>
      </span>
      <span className="block h-2.5 w-full overflow-hidden rounded-full bg-slate-100 sm:h-3 sm:flex-1">
        <span
          className={`block h-full rounded-full ${barva.startsWith("#") ? "" : barva}`}
          style={{ width: `${Math.max(0, Math.min(100, podil))}%`, background: barva.startsWith("#") ? barva : undefined }}
        />
      </span>
      <span className={`hidden w-10 shrink-0 text-right text-xl font-bold tabular-nums sm:block ${tonCisla}`}>{hodnota}</span>
    </div>
  );
}

// Spojka mezi kroky trychtýře s konverzí.
function SpojkaFunnelu({ procenta, napoveda }) {
  return (
    <div className="my-2 sm:my-1.5 sm:pl-[100px]">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-light px-2.5 py-0.5 text-[11.5px] font-bold text-slate-600">
        <ChevronDown className="h-3 w-3" />
        {procenta} % dál
        {napoveda && <InfoTip text={napoveda} />}
      </span>
    </div>
  );
}

// Dlaždice Funnel. Trychtýř jde shora dolů, konverze sedí na spojkách,
// kam leady došly je dole jako dlaždice. Čísla i nápovědy zůstávají stejné.
export function DlazdiceFunnel({ d, sheetLive, fapiLive }) {
  // Celý Funnel = 100 % lead-cohort pohled (leady vytvořené TENTO měsíc + jejich stage postup).
  // Matematika: Trialů = Deal + Prohra + Čekají (stage Trial bez rozhodnutí). Vždy sedí.
  const leads = d.leadsThisMonth || 0;
  const trials = d.trialLeadsThisMonth || 0;
  const deals = d.dealThisMonth || 0;
  const losses = d.lossThisMonth || 0;
  const pending = Math.max(0, trials - deals - losses);
  const nqLeads = d.nqLeadsThisMonth || 0;
  const leadToTrialPct = leads > 0 ? Math.round((trials / leads) * 100) : 0;
  const leadToNqPct = leads > 0 ? Math.round((nqLeads / leads) * 100) : 0;
  const trialToDealPct = trials > 0 ? Math.round((deals / trials) * 100) : 0;
  const successPct = (deals + losses) > 0 ? Math.round((deals / (deals + losses)) * 100) : 0;

  const zdroje = [];
  if (sheetLive) zdroje.push("Databáze");
  if (fapiLive) zdroje.push("FAPI");

  const pilulky = [];
  if (nqLeads > 0) pilulky.push({
    text: `${nqLeads} nekvalitních leadů, ${leadToNqPct} %`,
    napoveda: "Leady vytvořené tento měsíc, které skončily ve stage NQlead (nekvalitní, nesplňují kritéria, neodpovídají, nerealistické očekávání). Pomáhá měřit kvalitu MKT kampaní.",
  });

  return (
    <KartaPrehledu>
      <HlavickaDlazdice
        ikona={Activity}
        nazev="Funnel"
        odznak={zdroje.length > 0 ? zdroje.join(" a ") : "Mock"}
        odznakZivy={zdroje.length > 0}
      />
      <VelkeCislo hodnota={fmt(leads)} jednotka="leadů tento měsíc" pilulky={pilulky} />

      <SkupinaPrehledu ikona={Activity} nazev="Cesta leadu" />
      <div className="px-4 py-3 sm:px-5">
        <KrokFunnelu
          nazev="Leady"
          napoveda="Počet nových leadů vytvořených v tomto měsíci (podle data vytvoření leadu). Celý Funnel sleduje cohort těchto leadů, jak se jich kolik propadá skrze trial do dealu nebo prohry."
          hodnota={fmt(leads)}
          podil={100}
          barva={ODSTINY_ZNACKY[0]}
        />
        <SpojkaFunnelu
          procenta={leadToTrialPct}
          napoveda="Kolik procent leadů tohoto měsíce došlo až na zkušební trénink."
        />
        <KrokFunnelu
          nazev="Trialy"
          napoveda="Kolik z leadů vytvořených tento měsíc dosáhlo Trial stage nebo dál (Trial / Deal / Uzavřeno / Prohra). Lead-cohort, konzistentní s ostatními kroky Funnelu. Pozn.: počet FAPI trial faktur tento měsíc (trial-cohort) je v dlaždici Aktivní klienti pod Zaplaceno tento měsíc, může se lišit, pokud někdo zaplatil trial v jiném měsíci než ve kterém vznikl lead."
          hodnota={fmt(trials)}
          podil={leads > 0 ? (trials / leads) * 100 : 0}
          barva={ODSTINY_ZNACKY[1]}
        />
        <SpojkaFunnelu
          procenta={trialToDealPct}
          napoveda="Kolik procent trialů tohoto měsíce skončilo dealem."
        />
        <KrokFunnelu
          nazev="Dealy"
          napoveda="Leady vytvořené TENTO MĚSÍC, které už mají v Leady stage Deal nebo Uzavřeno. Pozn.: toto číslo může být nižší než Deal z trialu v dlaždici Aktivní klienti, ta používá trial-atribuci (klient měl trial TENTO měsíc, lead mohl vzniknout dřív). Funnel = lead-cohort pohled pro marketing, dlaždice = trial-cohort pohled pro operativu."
          hodnota={fmt(deals)}
          podil={leads > 0 ? (deals / leads) * 100 : 0}
          barva="bg-emerald-500"
          tonCisla="text-emerald-700"
        />
      </div>

      {(deals > 0 || losses > 0 || pending > 0) && (
        <>
          <SkupinaPrehledu ikona={CheckCircle2} nazev="Kde leady skončily" />
          <div className="px-3 py-3 sm:px-4">
            <MrizkaDlazdic sloupce={pending > 0 && losses > 0 ? "grid-cols-3" : "grid-cols-2"}>
              <MiniDlazdice
                hodnota={fmt(deals)}
                popisek="Deal"
                napoveda="Leady vytvořené TENTO MĚSÍC, které už mají v Leady stage Deal nebo Uzavřeno."
                ton="zelena"
              />
              {losses > 0 && (
                <MiniDlazdice
                  hodnota={fmt(losses)}
                  popisek="Prohra"
                  napoveda="Leady vytvořené TENTO MĚSÍC, které už mají v Leady stage Prohra. Stejně jako u Dealu, nepočítá prohry z leadů vzniklých dřív, jejichž trial proběhl teď (ty jsou v dlaždici Aktivní klienti pod Prohra)."
                  ton="cervena"
                />
              )}
              {pending > 0 && (
                <MiniDlazdice
                  hodnota={fmt(pending)}
                  popisek="Čekají"
                  napoveda="Leady vytvořené TENTO MĚSÍC, kteří prošli Trial stage ale ještě se o nich nerozhodlo (stage pořád Trial, ne Deal nebo Prohra). Jejich konverze se ukáže v dalších dnech. Součet Deal + Prohra + Čekají = Trialů (matematika Funnelu sedí)."
                  ton="slate"
                />
              )}
            </MrizkaDlazdic>
            {(deals > 0 || losses > 0) && (
              <SouhrnnyRadek
                className="mt-2"
                popisek="Úspěšnost"
                napoveda="Z rozhodnutých leadů vytvořených tento měsíc (Deal + Prohra) kolik skončilo dealem. Leady, které ještě čekají na rozhodnutí, se do úspěšnosti nepočítají, ale jsou v celkovém počtu Trialů. Lead-atribuce, ne trial-atribuce."
                doplnek={`${deals} z ${deals + losses} rozhodnutých`}
                hodnota={`${successPct} %`}
                ton={successPct >= 70 ? "zelena" : successPct >= 50 ? "zluta" : "cervena"}
              />
            )}
          </div>
        </>
      )}
    </KartaPrehledu>
  );
}

// Barva obsazenosti: pod 50 % rudá, 50 až 69 % oranžová, 70 až 80 % světle
// zelená, 81 % a víc tmavě zelená. Významy zůstávají stejné jako dosud.
function barvaObsazenosti(pct) {
  return pct >= 81 ? "#059669" : pct >= 70 ? "#16A34A" : pct >= 50 ? "#D97706" : "#DC2626";
}

// Dlaždice Kapacita. Lokality jsou řádky s pruhem, tabulka trenérů se na
// telefonu rozpadne na řádky a na počítači zůstane tabulkou.
export function DlazdiceKapacita({ d }) {
  const cap = d.capacity;
  const lokality = Object.entries(cap.capacityByLocation)
    .sort((a, b) => (b[0].toLowerCase().includes("vt") ? 1 : 0) - (a[0].toLowerCase().includes("vt") ? 1 : 0));
  // 28.5.2026: jen aktivní trenéři, musí mít lokalitu a odučené hodiny v okně.
  const treneri = [...cap.trainerBreakdown]
    .filter((t) => t.location && t.location.trim() && t.usedHoursPerWeek > 0 && t.capacityHours > 0)
    .sort((a, b) => (b.location.toLowerCase().includes("vt") ? 1 : 0) - (a.location.toLowerCase().includes("vt") ? 1 : 0) || b.utilizationPct - a.utilizationPct);

  return (
    <KartaPrehledu>
      <HlavickaDlazdice ikona={Landmark} nazev="Kapacita" odznak="Live z databáze" odznakZivy />

      <div className="px-4 py-4 sm:px-5">
        <div className="flex items-baseline gap-2 whitespace-nowrap">
          <span className="text-3xl font-bold leading-none tabular-nums text-slate-900 sm:text-4xl">
            {cap.avgUtilizationPct} %
          </span>
          <span className="text-lg font-semibold text-slate-500">průměrná obsazenost</span>
        </div>
        <div className="mt-2.5 h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${Math.min(cap.avgUtilizationPct, 100)}%`, backgroundColor: barvaObsazenosti(cap.avgUtilizationPct) }}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Pilulka>{cap.totalUsedPerWeek} z {cap.totalCapacityHours} hod týdně</Pilulka>
          <Pilulka>{treneri.length} trenérů</Pilulka>
        </div>
      </div>

      <SkupinaPrehledu ikona={Landmark} nazev="Podle lokality" />
      <RadkyPrehledu>
        {lokality.map(([nazev, loc]) => (
          <div key={nazev} className="py-2.5 sm:flex sm:items-center sm:gap-4">
            <span className="flex items-center justify-between gap-2 text-sm sm:w-40 sm:shrink-0">
              <span className="font-semibold text-slate-700">{nazev}</span>
              <span
                className="text-base font-bold tabular-nums sm:hidden"
                style={{ color: barvaObsazenosti(loc.utilizationPct) }}
              >
                {loc.utilizationPct} %
              </span>
            </span>
            <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-slate-200 sm:mt-0 sm:h-2 sm:flex-1">
              <span
                className="block h-full rounded-full"
                style={{ width: `${Math.min(loc.utilizationPct, 100)}%`, backgroundColor: barvaObsazenosti(loc.utilizationPct) }}
              />
            </span>
            <span className="mt-1.5 flex flex-wrap gap-1.5 sm:mt-0 sm:shrink-0">
              <Pilulka>{loc.usedHoursPerWeek} z {loc.capacityHours} hod</Pilulka>
              {loc.maxTrainers > 0
                ? <Pilulka>{loc.trainers.length} z {loc.maxTrainers} trenérů</Pilulka>
                : <Pilulka>{loc.trainers.length} trenérů</Pilulka>}
            </span>
            <span
              className="hidden w-12 shrink-0 text-right text-base font-bold tabular-nums sm:block"
              style={{ color: barvaObsazenosti(loc.utilizationPct) }}
            >
              {loc.utilizationPct} %
            </span>
          </div>
        ))}
      </RadkyPrehledu>

      <SkupinaPrehledu ikona={Users} nazev="Podle trenéra" />
      {/* Na telefonu řádky, na počítači tabulka se všemi šesti sloupci. */}
      <div className="divide-y divide-slate-200 px-4 sm:hidden">
        {treneri.map((t, i) => {
          const klienti = d.trainerStats ? d.trainerStats.find((ts) => ts.name === t.name) : null;
          return (
            <div key={i} className="py-2.5">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="font-semibold text-slate-700">{t.name}</span>
                <span className="text-base font-bold tabular-nums" style={{ color: barvaObsazenosti(t.utilizationPct) }}>
                  {t.utilizationPct} %
                </span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${Math.min(t.utilizationPct, 100)}%`, backgroundColor: barvaObsazenosti(t.utilizationPct) }}
                />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <Pilulka>{t.location.replace(" - Kbely", "")}</Pilulka>
                {klienti && <Pilulka>{klienti.clients} klientů</Pilulka>}
                <Pilulka>{t.usedHoursPerWeek} z {t.capacityHours} hod</Pilulka>
              </div>
            </div>
          );
        })}
      </div>
      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="px-5 py-2 text-left text-sm font-semibold text-slate-600">Trenér</th>
              <th className="px-5 py-2 text-left text-sm font-semibold text-slate-600">Lokalita</th>
              <th className="px-5 py-2 text-right text-sm font-semibold text-slate-600">Klienti</th>
              <th className="px-5 py-2 text-right text-sm font-semibold text-slate-600">Kapacita</th>
              <th className="px-5 py-2 text-right text-sm font-semibold text-slate-600">Využití</th>
              <th className="px-5 py-2 text-right text-sm font-semibold text-slate-600">Obsazenost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {treneri.map((t, i) => {
              const klienti = d.trainerStats ? d.trainerStats.find((ts) => ts.name === t.name) : null;
              return (
                <tr key={i}>
                  <td className="px-5 py-2 text-sm font-semibold text-slate-900">{t.name}</td>
                  <td className="px-5 py-2 text-sm text-slate-600">{t.location.replace(" - Kbely", "")}</td>
                  <td className="px-5 py-2 text-right text-sm font-bold tabular-nums text-slate-800">{klienti ? klienti.clients : "—"}</td>
                  <td className="px-5 py-2 text-right text-sm tabular-nums text-slate-700">{t.capacityHours} h</td>
                  <td className="px-5 py-2 text-right text-sm tabular-nums text-slate-700">{t.usedHoursPerWeek} h</td>
                  <td
                    className="px-5 py-2 text-right text-sm font-bold tabular-nums"
                    style={{ color: barvaObsazenosti(t.utilizationPct) }}
                  >
                    {t.utilizationPct} %
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {cap.hotHours && (
        <>
          <SkupinaPrehledu ikona={Clock} nazev="Volné hodiny" />
          <div className="px-3 py-3 sm:px-4">
            <MrizkaDlazdic sloupce="grid-cols-3">
              <MiniDlazdice
                hodnota={cap.hotHours.morning.freePerWeek}
                popisek={`Ráno ${cap.hotHours.morning.label}, volných hod týdně, obsazeno ${cap.hotHours.morning.usedPerWeek} hod`}
                ton="zluta"
              />
              <MiniDlazdice
                hodnota={cap.hotHours.afternoon.freePerWeek}
                popisek={`Odpoledne ${cap.hotHours.afternoon.label}, volných hod týdně, obsazeno ${cap.hotHours.afternoon.usedPerWeek} hod`}
                ton="znacka"
              />
              <MiniDlazdice
                hodnota={cap.freeCapacityPerWeek}
                popisek="Volná kapacita, hod týdně celkem"
                ton="zelena"
              />
            </MrizkaDlazdic>
          </div>
        </>
      )}
    </KartaPrehledu>
  );
}

// Aktuální měsíc v TZ uživatele (YYYY-MM)
function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// Format datum YYYY-MM-DD → "1. 9. 2026". Vždy s rokem, používá se u období.
function formatDatumCesky(iso) {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${parseInt(m[3], 10)}. ${parseInt(m[2], 10)}. ${m[1]}`;
}

// Format datum YYYY-MM-DD → "9. 5." pokud aktuální rok, jinak "9. 5. 2026". Plus relativní popisek.
function formatTrialDate(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const now = new Date();
  const sameYear = now.getFullYear() === parseInt(y, 10);
  return sameYear ? `${parseInt(d, 10)}. ${parseInt(mo, 10)}.` : `${parseInt(d, 10)}. ${parseInt(mo, 10)}. ${y}`;
}

function relativeDayLabel(iso) {
  if (!iso) return "";
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const target = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "dnes";
  if (diffDays === 1) return "zítra";
  if (diffDays > 1 && diffDays < 7) return `za ${diffDays} dní`;
  return "";
}

export default function Dashboard() {
  const currentMonth = getCurrentMonth();
  const { data, loading, error, refetch, refreshing, nactenoV } = useApiData("/api/data-overview");
  const { data: goalsFromApi } = useApiData(`/api/settings?month=${currentMonth}`);

  // goalsOverride = POST response (okamžitý stav), má přednost před API GET
  const [goalsOverride, setGoalsOverride] = useState(null);
  const goals = goalsOverride || goalsFromApi;

  // Demo režim — globální stav ze Sidebaru (Context)
  const { blurred } = useDemoMode();

  // Propojení alertů se zvonečkem v TopBaru (setAlertCount se volá i z NotificationsContext)
  const { sync: syncNotifications } = useNotifications();
  const alertItems = data?.undecidedAlerts || [];
  const missingInvoiceItems = data?.missingInvoiceAlerts || [];
  const missingReservioItems = data?.missingReservioAlerts || [];
  const roiItems = data?.roiAlerts || [];
  const cplItems = data?.cplAlerts || [];

  // Po načtení data-overview pošli raw alerty do backendu notifikací (sync)
  useEffect(() => {
    if (!data) return;
    syncNotifications({
      undecided: alertItems,
      missing_invoice: missingInvoiceItems,
      missing_reservio: missingReservioItems,
      roi_low: roiItems,
      cpl_high: cplItems,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.generatedAt]);

  // handleRefresh = refetch s vizuálním feedbackem (refreshing stav z hooku)

  // Uložení cíle pro aktuální měsíc, okamžitá aktualizace UI
  const saveGoal = useCallback(async (key, value) => {
    try {
      const res = await apiFetch("/api/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ month: currentMonth, [key]: value }),
      });
      const saved = await res.json();
      // Pokud server vrátil error, neměníme UI, zachováme aktuální stav
      if (saved.error) {
        console.error("Settings save error:", saved.error, saved._errorDetail);
        toast(`Chyba ukládání cíle: ${saved.error}`, "error");
        return;
      }
      // Okamžitě přepíšeme React stav, POST response obsahuje OBĚ hodnoty
      setGoalsOverride(saved);
      // Sync cache
      const cacheKey = `api_cache_/api/settings?month=${currentMonth}`;
      try { localStorage.setItem(cacheKey, JSON.stringify(saved)); } catch {}
    } catch (e) {
      console.error("Settings fetch error:", e);
      toast(`Chyba při ukládání: ${e.message}`, "error");
    }
  }, [currentMonth]);

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
          <span className="ml-3 text-sm text-slate-500">Načítám data z backendu…</span>
        </div>
      </div>
    );
  }

  // 8.9.2026: chybová stránka jen tehdy, když opravdu není co ukázat. Když jsou
  // v paměti data z minule, Přehled se s nimi vykreslí a nahoře je jen úzký pruh
  // (viz níže). Síťová chyba se navíc dvakrát opakuje už v useApiData.
  const sitovyVypadek = /load failed|failed to fetch|network/i.test(String(error || ""));
  if (error && !data) {
    return (
      <div className="p-4 sm:p-6">
        <Card title="Data se nepodařilo načíst">
          <div className="text-sm text-slate-600 mb-3">
            {sitovyVypadek
              ? "Zkoušel jsem to třikrát a spojení se pokaždé přerušilo. Zkontroluj připojení a zkus to znovu."
              : `Přehled se nepodařilo načíst: ${error}`}
          </div>
          <button
            onClick={refetch}
            disabled={refreshing}
            className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white sm:w-auto"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Zkusit znovu
          </button>
        </Card>
      </div>
    );
  }

  // fallback na mock data pro pole, která ještě nejsou v backend odpovědi
  const d = { ...mockOverview, ...data };

  const notionLive = d.sources?.notion?.ok;
  const metaLive = d.sources?.meta?.ok;
  const fapiLive = d.sources?.fapi?.ok;
  const bankLive = d.sources?.bank?.ok;
  const sheetLive = d.sources?.sheet?.ok;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* Nová data nedorazila, ale máme uložená z minula — ukážeme je a řekneme, jak jsou stará. */}
      {error && data && (
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1 text-[13px] text-amber-800">
            Nová data nedorazila{nactenoV ? `, ukazuju stav z ${new Date(nactenoV).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}` : ", ukazuju poslední uložená"}.
          </span>
          <button
            type="button"
            onClick={refetch}
            disabled={refreshing}
            className="shrink-0 rounded-xl border border-amber-300 bg-white px-2.5 py-1 text-[13px] font-semibold text-amber-800 min-h-[44px] sm:min-h-0"
          >
            {refreshing ? "Načítám…" : "Zkusit znovu"}
          </button>
        </div>
      )}

      {/* 9.9.2026 (fáze 4f): Přehled dostal stejnou hlavičku jako ostatní
          stránky. Obnovit bylo drobné šedé tlačítko zarovnané doprava, teď je
          ikona v hlavičce (rozhodnutí z fáze 0). */}
      <GradientHeader
        title="Přehled"
        subtitle="Klíčová čísla firmy k dnešnímu dni."
        icon={LayoutDashboard}
        loading={refreshing}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing }]}
      />

      {/* Demo wrapper, skryje pouze čísla, ne texty */}
      <div className={`space-y-6 ${blurred ? "demo-blur" : ""}`}>
      {blurred && (
        <style>{`
          .demo-blur .tabular-nums,
          .demo-blur .recharts-text,
          .demo-blur .recharts-cartesian-axis-tick-value,
          .demo-blur .recharts-tooltip-wrapper,
          .demo-blur .recharts-bar,
          .demo-blur .recharts-pie,
          .demo-blur td.font-medium,
          .demo-blur .demo-sensitive {
            filter: blur(8px);
            user-select: none;
          }
        `}</style>
      )}

      {/* 14.9.2026: Priority měsíce dřív braly celý pruh nad přehledem a Cíle
          byly zvlášť v krajní dlaždici. Teď jsou v jedné dlaždici, priority
          nahoře, cíle pod nimi, a horní pruh je volný. */}

      {/* === KPI řádek 1 === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <DlazdiceAktivniKlienti d={d} sheetLive={sheetLive} notionLive={notionLive} />
        <DlazdiceRevenue d={d} fapiLive={fapiLive} />
        <MesicniPriority
          mesic={currentMonth}
          highlight
          nazev="Priority a cíle"
          kompaktni
          pod={
            <GoalsCard
              vnorene
              monthlyGoal={goals?.monthlyGoal || 0}
              monthlyRevenue={d.monthlyRevenueLive}
              monthlyGoalPct={goals?.monthlyGoal > 0 ? Math.round((d.monthlyRevenueLive / goals.monthlyGoal) * 100) : 0}
              newClientGoal={goals?.newClientGoal || 0}
              newClientRevenue={d.newClientRevenue || 0}
              newClientGoalPct={goals?.newClientGoal > 0 ? Math.round(((d.newClientRevenue || 0) / goals.newClientGoal) * 100) : 0}
              onSaveMonthlyGoal={(v) => saveGoal("monthlyGoal", v)}
              onSaveNewClientGoal={(v) => saveGoal("newClientGoal", v)}
              readOnly={getCurrentUser()?.role !== "owner"}
              isNewMonth={goals?.isNewMonth || false}
              month={goals?.month || currentMonth}
            />
          }
        />
      </div>

      {/* === KPI řádek 2, MKT + Trialy + Zdroje === */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <DlazdiceMkt d={d} metaLive={metaLive} />
        <DlazdiceTrialy d={d} zivo={notionLive || sheetLive} />

        <DlazdiceZdroje d={d} sheetLive={sheetLive} />
      </div>

      {/* 30. 9. 2026: sales za minulý týden (owner, manažer), mockup v8. */}
      {["owner", "manager"].includes(String(getCurrentUser()?.role || "").toLowerCase()) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <DlazdiceSales />
        </div>
      )}

      {/* === Funnel + Kapacita (pod sebou) === */}
      {/* Vždy zobrazit Funnel, i s 0 hodnotami (na začátku měsíce). Placeholder řeší prázdný stav uvnitř. */}
      {(
        <div className="space-y-6">
          <DlazdiceFunnel d={d} sheetLive={sheetLive} fapiLive={fapiLive} />

          {d.capacity && <DlazdiceKapacita d={d} />}
        </div>
      )}

      {/* === Notifikace === */}
      <div id="alerts-section">
        <NotificationsPanel />
      </div>

      </div>{/* /blur wrapper */}
    </div>
  );
}
